/**
 * ElencoIscrittiCompatto — gli iscritti di un questionario sul telefono (sotto i 900 px).
 *
 * La tabella ha dieci colonne (larga 1.100 px): sul telefono se ne vedevano due alla volta. Qui ogni iscritto è
 * una riga: nome e azienda in testa, ruolo, poi data, provenienza e consenso; sotto, email e telefono come
 * collegamenti, perché dal telefono la cosa che si fa con un iscritto è scrivergli o chiamarlo. Toccare la riga
 * apre la scheda, come sul computer. Stessi dati e stessi filtri della tabella (li applica SchedaIscritti),
 * compreso «a che punto è» (lo stato del percorso, dal registro delle azioni).
 */

import React from 'react';
import { Box, ButtonBase, Link, Stack, Typography } from '@mui/material';
import { MailOutline, PhoneOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme';
import { SoftChip } from '../../SoftChip';
import type { PlatformLead, PlatformQuestionnaireResponse } from '../../../types/platformCrm';
import { CONSENSO_LABELS, CONSENSO_TONES, formatDate, formatDateTime, STATO_LABELS, STATO_TONES } from '../labels';
import { contattiDi, provenienzaDi, statoConsenso } from './filtri';

interface Props {
  iscritti: PlatformQuestionnaireResponse[];
  lead: Map<string, PlatformLead>;
  vuoto: string;
  onApriLead: (lead: PlatformLead) => void;
}

export const ElencoIscrittiCompatto: React.FC<Props> = ({ iscritti, lead, vuoto, onApriLead }) => {
  const { t } = useTranslation('crm');
  if (iscritti.length === 0) {
    return (
      <Typography variant="body2" sx={{ p: 4, textAlign: 'center', color: tokens.color.mist }}>{vuoto}</Typography>
    );
  }

  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
      {iscritti.map((r) => {
        const l = lead.get(r.platform_lead_id);
        const c = contattiDi(r, l);
        const stato = statoConsenso(l);
        const intestazione = [c.nome, c.azienda].filter(Boolean).join(', ') || '—';
        return (
          <Box
            component="li"
            key={r.id}
            sx={{ '&:not(:last-of-type)': { borderBottom: `1px solid ${tokens.color.lineSoft}` } }}
          >
            <ButtonBase
              onClick={() => l && onApriLead(l)}
              disabled={!l}
              title={l ? t('iscritti.apri_scheda') : t('iscritti.scheda_non_leggibile')}
              sx={{ display: 'block', width: '100%', textAlign: 'left', px: 2, pt: 1.5, pb: 1 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, color: tokens.color.graphite, lineHeight: 1.3 }}>
                {intestazione}
              </Typography>
              {c.ruolo && (
                <Typography variant="caption" sx={{ color: tokens.color.ash, display: 'block' }}>{c.ruolo}</Typography>
              )}
              <Stack direction="row" useFlexGap spacing={0.75} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ color: tokens.color.mist, whiteSpace: 'nowrap' }}>
                  {formatDateTime(r.completato_at)}
                </Typography>
                {l && <SoftChip label={STATO_LABELS[l.stato]} tone={STATO_TONES[l.stato]} />}
                <SoftChip label={provenienzaDi(r)} tone="default" outlined />
                <SoftChip label={CONSENSO_LABELS[stato]} tone={CONSENSO_TONES[stato]} />
                {l?.non_contattare && <SoftChip label={t('iscritti.non_contattare_compatto', { data: formatDate(l.non_contattare_at) })} tone="error" />}
              </Stack>
            </ButtonBase>
            {(c.email || c.telefono) && (
              // ⚠️ Fuori dal bottone della riga: un collegamento dentro un bottone è un bersaglio annidato, e il
              // tocco su «chiama» aprirebbe anche la scheda.
              <Stack direction="row" sx={{ px: 1, pb: 1, flexWrap: 'wrap' }}>
                {c.email && (
                  <Link
                    href={`mailto:${c.email}`}
                    underline="none"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minHeight: 40, px: 1, fontSize: '0.85rem', overflowWrap: 'anywhere' }}
                  >
                    <MailOutline sx={{ fontSize: 18 }} />{c.email}
                  </Link>
                )}
                {c.telefono && (
                  <Link
                    href={`tel:${c.telefono.replace(/\s+/g, '')}`}
                    underline="none"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, minHeight: 40, px: 1, fontSize: '0.85rem', whiteSpace: 'nowrap' }}
                  >
                    <PhoneOutlined sx={{ fontSize: 18 }} />{c.telefono}
                  </Link>
                )}
              </Stack>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
